import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { partsApi, formatPrice, getFileUrl, PART_CATEGORIES, PART_CONDITIONS } from "@/lib/api";
import { 
  Search,
  Package,
  MapPin,
  Filter,
  Phone,
  MessageCircle,
  Mail
} from "lucide-react";

export default function PartsShowroomPage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const data = await partsApi.getShowroom();
      setParts(data);
    } catch (error) {
      console.error("Failed to fetch parts");
    } finally {
      setLoading(false);
    }
  };

  const filteredParts = parts.filter(part => {
    const matchesSearch = !searchTerm || 
      part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.compatible_models.some(m => m.toLowerCase().includes(searchTerm.toLowerCase())) ||
      part.compatible_brands.some(b => b.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = categoryFilter === "all" || part.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const whatsappNumber = "40792250058";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Bonjour! Je suis intéressé par vos pièces détachées.")}`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="font-heading font-black text-white">S</span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg">SCUTERSH</h1>
                <p className="text-xs text-muted-foreground">Pièces Détachées</p>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/showroom">
                <Button variant="outline" size="sm">
                  Scooters
                </Button>
              </Link>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-600 hover:bg-green-700" size="sm">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contact
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="font-heading font-black text-4xl md:text-5xl mb-4">
            Pièces Détachées
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Pièces d'occasion de qualité pour scooters. Toutes nos pièces sont testées et garanties.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une pièce, marque ou modèle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full md:w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {Object.entries(PART_CATEGORIES).map(([key, { label, icon }]) => (
                <SelectItem key={key} value={key}>
                  {icon} {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Parts Count */}
        <p className="text-muted-foreground mb-6">
          {filteredParts.length} pièce{filteredParts.length !== 1 ? 's' : ''} disponible{filteredParts.length !== 1 ? 's' : ''}
        </p>

        {/* Parts Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-primary font-mono">Chargement...</div>
          </div>
        ) : filteredParts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredParts.map((part) => (
              <PartShowroomCard key={part.id} part={part} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-xl font-medium mb-2">Aucune pièce trouvée</p>
            <p className="text-muted-foreground">
              {searchTerm || categoryFilter !== "all" 
                ? "Essayez avec d'autres critères de recherche"
                : "Revenez bientôt pour voir nos nouvelles pièces"}
            </p>
          </div>
        )}
      </main>

      {/* Contact Section */}
      <section className="bg-card border-t border-border py-16 mt-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl mb-4">
              Besoin d'une pièce spécifique ?
            </h2>
            <p className="text-muted-foreground mb-8">
              Contactez-nous et nous vous aiderons à trouver la pièce qu'il vous faut.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a href="tel:+40792250058">
                <Button variant="outline" className="w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  +40 792 250 058
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary/50 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="font-heading font-bold text-lg mb-2">SCUTERSH</p>
          <p className="text-sm text-muted-foreground">
            Scooters premium et pièces détachées de qualité
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/showroom" className="text-sm text-primary hover:underline">
              Voir les scooters
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/parts-showroom" className="text-sm text-primary hover:underline">
              Voir les pièces
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PartShowroomCard({ part }) {
  const categoryInfo = PART_CATEGORIES[part.category] || PART_CATEGORIES.autres;
  const conditionInfo = PART_CONDITIONS[part.condition] || PART_CONDITIONS.bon;
  const defaultImage = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop";
  
  const whatsappNumber = "40792250058";
  const whatsappMessage = `Bonjour! Je suis intéressé par: ${part.name} (${part.part_id})`;
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <Card className="overflow-hidden hover:border-primary/50 transition-colors group">
      <Link to={`/part/${part.part_id}`}>
        <div className="aspect-[4/3] relative overflow-hidden bg-secondary">
          <img
            src={part.photos?.[0] ? getFileUrl(part.photos[0]) : defaultImage}
            alt={part.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-2 left-2">
            <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-mono">
              {part.part_id}
            </span>
          </div>
          <div className="absolute top-2 right-2">
            <span className={`px-2 py-1 rounded text-xs text-white ${conditionInfo.color}`}>
              {conditionInfo.label}
            </span>
          </div>
        </div>
      </Link>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">{categoryInfo.icon}</span>
          <span className="text-xs text-muted-foreground">{categoryInfo.label}</span>
        </div>
        <Link to={`/part/${part.part_id}`}>
          <h3 className="font-heading font-bold truncate hover:text-primary transition-colors">
            {part.name}
          </h3>
        </Link>
        <p className="text-sm text-muted-foreground truncate mb-3">
          {part.compatible_brands.slice(0, 2).join(", ")} • {part.compatible_years.slice(0, 3).join(", ")}
        </p>
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono font-bold text-xl text-primary">{formatPrice(part.price)}</p>
          <p className="text-sm text-muted-foreground">Qté: {part.quantity}</p>
        </div>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block">
          <Button variant="outline" size="sm" className="w-full">
            <MessageCircle className="w-4 h-4 mr-2" />
            Contact
          </Button>
        </a>
      </CardContent>
    </Card>
  );
}
